import type { Logout } from 'flowhawk-core';
import type { Reducer } from '@reduxjs/toolkit';
import type { ResetData } from './ApplicationActions';
import type { UserProfile, Team } from '../Utils/types';

export type TeamErrors = {
  teamName?: string;
  nonFieldErrors?: string;
};

export type TeamState = {
  teams: Array<Team>;
  teamFilter: string;

  teamName: string;
  teamMembers: Array<UserProfile>;
  shared: boolean;

  teamStateHasChanged: boolean;

  errors?: TeamErrors;
};

const initialState: TeamState = {
  teams: [],
  teamFilter: '',

  teamName: '',
  teamMembers: [],
  shared: false,

  teamStateHasChanged: false,

  errors: undefined,
};

export type GetTeams = { type: 'Team/GET_TEAMS' };
export type ReceiveTeams = { type: 'Team/RECEIVE_TEAMS'; teams: Array<Team> };

export type FilterTeams = { type: 'Team/FILTER'; teamFilter: string };

export type SetTeamName = { type: 'Team/SET_TEAM_NAME'; teamName: string };
export type SetTeamMembers = { type: 'Team/SET_TEAM_MEMBERS'; teamMembers: Array<UserProfile> };
export type SetShared = { type: 'Team/SET_SHARED'; shared: boolean };
export type CreateTeam = {
  type: 'Team/CREATE_TEAM';
  teamName: string;
  teamMembers: Array<UserProfile>;
  shared: boolean;
};
export type CreateTeamFailed = { type: 'Team/CREATE_TEAM_FAILED'; errors?: TeamErrors };
export type TeamChanged = { type: 'Team/TEAM_CHANGED'; teamStateHasChanged: boolean };

export type UpdateTeam = {
  type: 'Team/UPDATE';
  teamId: number;
  teamName: string;
  teamMembers: Array<UserProfile>;
  shared: boolean;
};
export type DeleteTeam = { type: 'Team/DELETE_TEAM'; teamId: number };

export type ResetTeamEditState = { type: 'Team/RESET' };

type Action =
  | GetTeams
  | ReceiveTeams
  | FilterTeams
  | SetTeamName
  | SetTeamMembers
  | SetShared
  | CreateTeam
  | CreateTeamFailed
  | TeamChanged
  | UpdateTeam
  | DeleteTeam
  | ResetTeamEditState
  | ResetData
  | Logout;

export const getTeams = (): GetTeams => ({
  type: 'Team/GET_TEAMS',
});

export const receiveTeams = (teams: Array<Team>): ReceiveTeams => ({
  type: 'Team/RECEIVE_TEAMS',
  teams,
});

export const filterTeams = (teamFilter: string): FilterTeams => ({
  type: 'Team/FILTER',
  teamFilter,
});

export const setTeamName = (teamName: string): SetTeamName => ({
  type: 'Team/SET_TEAM_NAME',
  teamName,
});

export const setTeamMembers = (teamMembers: Array<UserProfile>): SetTeamMembers => ({
  type: 'Team/SET_TEAM_MEMBERS',
  teamMembers,
});

export const setShared = (shared: boolean): SetShared => ({
  type: 'Team/SET_SHARED',
  shared,
});

export const createTeam = (teamName: string, teamMembers: Array<UserProfile>, shared: boolean): CreateTeam => ({
  type: 'Team/CREATE_TEAM',
  teamName,
  teamMembers,
  shared,
});

export const createTeamFailed = (errors?: TeamErrors): CreateTeamFailed => ({
  type: 'Team/CREATE_TEAM_FAILED',
  errors,
});

export const teamChanged = (teamStateHasChanged: boolean): TeamChanged => ({
  type: 'Team/TEAM_CHANGED',
  teamStateHasChanged,
});

export const updateTeam = (
  teamId: number,
  teamName: string,
  teamMembers: Array<UserProfile>,
  shared: boolean,
): UpdateTeam => ({
  type: 'Team/UPDATE',
  teamId,
  teamName,
  teamMembers,
  shared,
});

export const deleteTeam = (teamId: number): DeleteTeam => ({
  type: 'Team/DELETE_TEAM',
  teamId,
});

export const resetTeamEditState = (): ResetTeamEditState => ({
  type: 'Team/RESET',
});

const reducer = (state: TeamState = initialState, action: Action): TeamState => {
  switch (action.type) {
    case 'Application/RESET_DATA':
    case 'Auth/LOGOUT':
      // Reset the store when logging out
      return {
        ...initialState,
      };

    case 'Team/RECEIVE_TEAMS':
      return {
        ...state,

        teams: action.teams.slice(),
      };

    case 'Team/FILTER':
      return {
        ...state,

        teamFilter: action.teamFilter,
      };

    case 'Team/SET_TEAM_NAME':
      return {
        ...state,

        teamName: action.teamName,
      };

    case 'Team/SET_TEAM_MEMBERS':
      return {
        ...state,

        teamMembers: action.teamMembers.slice(),
      };

    case 'Team/SET_SHARED':
      return {
        ...state,

        shared: action.shared,
      };

    case 'Team/CREATE_TEAM_FAILED':
      return {
        ...state,

        errors: action.errors,
      };

    case 'Team/TEAM_CHANGED':
      return {
        ...state,

        teamStateHasChanged: action.teamStateHasChanged,
      };

    case 'Team/RESET':
      return {
        ...state,

        teamName: initialState.teamName,
        teamMembers: initialState.teamMembers,
        shared: initialState.shared,
        errors: undefined,
      };

    default:
      return state;
  }
};

export default reducer as Reducer<TeamState>;
